import React, { useState } from 'react';

const PerformanceSummaryView = ({ tests, results }) => {
  const [expandedTestIds, setExpandedTestIds] = useState([]);

  if (results.length === 0) {
    return (
      <div id="performance-summary" className="mt-10 text-center text-slate-400 p-8 bg-slate-900/50 rounded-lg">
        <h3 className="text-xl font-bold text-indigo-400 mb-2">Performance Summary</h3>
        <p>No test results yet. Complete a test to see your performance summary.</p>
      </div>
    );
  }

  const toggleHistory = (testId) => {
    setExpandedTestIds(prev =>
      prev.includes(testId)
        ? prev.filter(id => id !== testId)
        : [...prev, testId]
    );
  };

  const performanceData = tests.map(test => {
    const testResults = results.filter(r => r.testId === test.id);
    if (testResults.length === 0) return null;

    const totalScore = testResults.reduce((sum, r) => sum + r.score, 0);
    const totalPossible = testResults.reduce((sum, r) => sum + r.totalQuestions, 0);
    const averageScore = totalPossible > 0 ? Math.round((totalScore / totalPossible) * 100) : 0;
    
    let passes = 0;
    if (test.passingScore) {
      passes = testResults.filter(r => (r.score / r.totalQuestions) * 100 >= test.passingScore).length;
    }
    
    return {
      testId: test.id,
      testName: test.name,
      attempts: testResults.length,
      averageScore,
      passes,
      fails: testResults.length - passes,
      hasPassingCriteria: !!test.passingScore,
      passingScore: test.passingScore,
    };
  }).filter(data => data !== null);

  if (performanceData.length === 0) {
    return (
      <div id="performance-summary" className="mt-10 text-center text-slate-400 p-8 bg-slate-900/50 rounded-lg">
        <h3 className="text-xl font-bold text-indigo-400 mb-2">Performance Summary</h3>
        <p>No results found for the currently available tests.</p>
      </div>
    );
  }

  return (
    <div id="performance-summary" className="mt-10">
      <h3 className="text-2xl font-bold text-indigo-400 mb-6 pb-4 border-b border-slate-700">Performance Summary</h3>
      <div className="space-y-6">
        {performanceData.map(data => {
          const isExpanded = expandedTestIds.includes(data.testId);
          const testAttempts = results
            .filter(r => r.testId === data.testId)
            .sort((a, b) => b.date - a.date);

          return (
            <div key={data.testId} className="bg-slate-700/50 p-4 rounded-lg border border-slate-700">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center">
                <h4 className="font-bold text-lg text-slate-200">{data.testName}</h4>
                <span className="text-sm text-slate-400 mt-1 sm:mt-0">{data.attempts} attempt(s)</span>
              </div>

              {data.hasPassingCriteria && (
                <div className="flex space-x-4 text-sm mt-2">
                  <span className="font-medium text-green-400">Passes: {data.passes}</span>
                  <span className="font-medium text-red-400">Fails: {data.fails}</span>
                </div>
              )}

              <div className="mt-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-slate-300">Average Score</span>
                  <span className="text-sm font-bold text-indigo-300">{data.averageScore}%</span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2.5">
                  <div 
                    className="bg-indigo-500 h-2.5 rounded-full transition-all duration-500" 
                    style={{ width: `${data.averageScore}%` }}
                  ></div>
                </div>
              </div>
              
              {testAttempts.length > 0 && (
                <div className="mt-4">
                  <button 
                    onClick={() => toggleHistory(data.testId)} 
                    className="text-sm text-indigo-400 hover:text-indigo-300 font-semibold"
                  >
                    {isExpanded ? 'Hide History' : 'Show History'}
                  </button>
                </div>
              )}
              
              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-slate-600">
                  <h5 className="text-sm font-semibold text-slate-300 mb-2">Attempt History</h5>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                    {testAttempts.map(result => {
                      const percentage = result.totalQuestions > 0 ? Math.round((result.score / result.totalQuestions) * 100) : 0;
                      const passed = data.hasPassingCriteria ? percentage >= data.passingScore : null;

                      return (
                        <div key={result.id} className="grid grid-cols-3 items-center text-xs sm:text-sm bg-slate-800 p-2 rounded-md gap-2">
                          <span className="text-slate-400">{new Date(result.date).toLocaleString()}</span>
                          <span className="text-slate-200 font-medium text-center">
                            {result.score} / {result.totalQuestions} ({percentage}%)
                          </span>
                          {passed !== null && (
                            <span className={`font-bold text-right ${passed ? 'text-green-400' : 'text-red-400'}`}>
                              {passed ? 'Pass' : 'Fail'}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PerformanceSummaryView;